import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Incident, IncidentSummary, Silence } from '../api/types';
import { IncidentDrawer } from '../components/IncidentDrawer';
import { LoadIssue } from '../components/LoadIssue';
import { ScreenLink } from '../components/ScreenLink';
import { ago, clock, duration } from '../lib/format';
import { can, useMe } from '../lib/me';
import { readParams, withParam, writeUrl } from '../lib/urlState';
import { useLoad } from '../lib/useLoad';

/** What a silence covers, in the words the board uses. */
const silenceScope = (silence: Silence) => {
  switch (silence.scope) {
    case 'global':
      return 'every incident';
    case 'system':
      return `everything on ${silence.system}`;
    case 'check':
      return `${silence.checkId}${silence.tenantKey ? ` for ${silence.tenantKey}` : ''}`;
    case 'tenant':
      return `everything about ${silence.tenantKey}`;
    case 'incident':
      return `incident #${silence.incidentId}`;
    default:
      return silence.scope;
  }
};

/**
 * The incident board -- the screen someone opens at 3am, and the only one whose
 * layout is argued rather than chosen.
 *
 * Every row answers three questions in the order a responder asks them: what is
 * broken, whose it is, and what to DO about it. The remedy is on the row itself
 * rather than behind a click, because the click is the thing that does not
 * happen when you have been woken up. Everything after that -- who was paged,
 * what happened since, where to look, silence or resolve -- is one click away,
 * in the incident's own drawer (components/IncidentDrawer.tsx), at its own
 * address.
 *
 * Polls every 15s and keeps the previous rows on screen while it does -- and
 * when a poll FAILS, which used to replace the board with a red line. A board
 * that blanks itself cannot be read.
 *
 * And it says whether the monitor is LOOKING. An empty board is only good news
 * if checks are running: a stalled scheduler or a blind check opens no
 * incident, so it used to look exactly like a quiet night here.
 */
export function Now() {
  const me = useMe();
  const canRespond = can(me, 'incidents.respond');
  const [busy, setBusy] = useState<number | null>(null);
  const [problems, setProblems] = useState<Record<number, string>>({});
  const [openId, setOpenId] = useState<number | null>(() => Number(readParams().get('incident')) || null);

  const board = useLoad(() => api.get<{ incidents: Incident[] }>('/incidents'), [], 15_000);
  const summary = useLoad(() => api.get<IncidentSummary>('/incidents/summary'), [], 15_000);
  const silences = useLoad(() => api.get<{ silences: Silence[] }>('/silences'), [], 30_000);

  // The drawer's incident is the URL's, so it survives a reload and can be
  // pasted into a chat.
  useEffect(() => {
    writeUrl(withParam(readParams(), 'incident', openId ? String(openId) : null));
  }, [openId]);

  const refreshAll = () => {
    void board.reload();
    void summary.reload();
    void silences.reload();
  };

  const acknowledge = async (incident: Incident) => {
    setBusy(incident.id);
    setProblems(({ [incident.id]: _gone, ...rest }) => rest);
    try {
      await api.post(`/incidents/${incident.id}/ack`);
      await board.reload();
    } catch (caught) {
      // Usually a teammate got there first (409 "not open"): say so on the
      // row, and re-read so it shows who.
      setProblems((current) => ({ ...current, [incident.id]: (caught as Error).message }));
      void board.reload();
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (silence: Silence) => {
    try {
      await api.del(`/silences/${silence.id}`);
    } finally {
      refreshAll();
    }
  };

  const monitor = summary.data?.monitor;
  const watching = (
    <div className="wt-monitor">
      <LoadIssue
        error={summary.error}
        updatedAt={summary.updatedAt}
        what="whether the monitor is running"
        onRetry={() => void summary.reload()}
      />
      {monitor?.stalled && (
        <div className="wt-banner wt-banner-danger" role="alert">
          <strong>sphere-watch is not looking.</strong>{' '}
          {monitor.lastRunAt
            ? `No check has recorded a run for ${duration(monitor.lastRunAgeMs ?? 0)} (last at ${clock(monitor.lastRunAt)}).`
            : 'No check has recorded a run at all.'}{' '}
          Nothing on this board is current, and nothing new will open.{' '}
          {can(me, 'systems.view') && (
            <ScreenLink to="/systems" previewTab="systems">
              Systems
            </ScreenLink>
          )}
        </div>
      )}
      {monitor && monitor.blind.length > 0 && (
        <div className="wt-banner wt-banner-warn">
          {monitor.blind.length === 1 ? 'One check cannot run' : `${monitor.blind.length} checks cannot run`} — a
          column moved or a grant was revoked, so whatever they watch could break without an incident here:
          <ul>
            {monitor.blind.slice(0, 5).map((check) => (
              <li key={check.id}>
                {check.title} <code>{check.id}</code>
              </li>
            ))}
          </ul>
          {can(me, 'checks.view') && (
            <ScreenLink to="/checks" previewTab="checks">
              See them on Checks
            </ScreenLink>
          )}
        </div>
      )}
    </div>
  );

  const active = silences.data?.silences ?? [];
  const silenceStrip =
    active.length > 0 ? (
      <details className="wt-silences">
        <summary>
          {active.length === 1 ? 'One silence is' : `${active.length} silences are`} holding pages back — until{' '}
          {clock(active[0].expiresAt)}
          {active.length > 1 ? ' and later' : ''}
        </summary>
        <ul>
          {active.map((silence) => (
            <li key={silence.id}>
              <strong>{silenceScope(silence)}</strong>
              <span>“{silence.reason}”</span>
              <span className="wt-when">
                {silence.createdBy} · until {new Date(silence.expiresAt).toLocaleString()}
              </span>
              <span className="wt-spacer" />
              {canRespond && (
                <button type="button" className="btn btn-ghost" onClick={() => void revoke(silence)}>
                  End it now
                </button>
              )}
            </li>
          ))}
        </ul>
      </details>
    ) : null;

  const drawer = openId ? (
    <IncidentDrawer id={openId} onClose={() => setOpenId(null)} onChanged={refreshAll} />
  ) : null;

  if (board.loading) return <div className="wt-empty">Loading…</div>;
  if (!board.data) {
    return (
      <>
        {watching}
        <LoadIssue error={board.error} updatedAt={null} what="incidents" onRetry={() => void board.reload()} />
        {drawer}
      </>
    );
  }

  const shown = board.data.incidents.filter((incident) => incident.status !== 'resolved');
  if (!shown.length) {
    return (
      <>
        {watching}
        <LoadIssue error={board.error} updatedAt={board.updatedAt} what="incidents" onRetry={() => void board.reload()} />
        {silenceStrip}
        <div className="wt-empty">
          <div className="big">Nothing is open.</div>
          {/* Deliberately not "all systems healthy". This board shows what the
              checks OBSERVED; a check that is blind reports unknown and is named
              above, not opened here. Claiming health we did not measure is the
              one thing this product must never do. */}
          <div>
            {monitor?.stalled
              ? 'But see above: no check is running, so this says nothing about the systems.'
              : 'No check has an open incident. The Checks screen shows what is actually being watched.'}
          </div>
        </div>
        {drawer}
      </>
    );
  }

  return (
    <>
      <div className="wt-head">
        <h1>Now</h1>
        <span className="sub">
          {shown.length} open · {shown.filter((i) => i.severity === 'critical').length} critical ·{' '}
          {shown.filter((i) => i.status === 'open').length} not acknowledged
        </span>
      </div>
      {watching}
      <LoadIssue error={board.error} updatedAt={board.updatedAt} what="incidents" onRetry={() => void board.reload()} />
      {silenceStrip}
      {shown.map((incident) => (
        <article key={incident.id} className={`wt-inc wt-sev-${incident.severity}`} aria-label={incident.title}>
          <div className="wt-inc-rule" />
          <div className="wt-inc-body">
            <div className="wt-inc-title">
              <span className={`wt-sev-label wt-sev-label-${incident.severity}`}>{incident.severity}</span>
              <button
                type="button"
                className="wt-inc-open"
                title="Open: who was told, what happened, where to look"
                onClick={() => setOpenId(incident.id)}
              >
                <strong>{incident.title}</strong>
              </button>
              {incident.tenantKey &&
                (can(me, 'tenants.view') ? (
                  <ScreenLink
                    to="/tenants"
                    previewTab="tenants"
                    search={`tenant=${encodeURIComponent(incident.tenantKey)}`}
                    className="wt-chip wt-chip-link"
                    title={`${incident.tenantKey} on Tenants`}
                  >
                    {incident.tenantKey}
                  </ScreenLink>
                ) : (
                  <span className="wt-chip">{incident.tenantKey}</span>
                ))}
              {incident.ref && <span className="wt-chip">{incident.ref}</span>}
              {/* A shadow incident is recorded but never alerted. Saying so on
                  the row stops someone concluding the alerting is broken. */}
              {incident.shadow && <span className="wt-chip wt-chip-shadow">shadow · not alerting</span>}
              {incident.flapping && <span className="wt-chip wt-chip-flap">flapping</span>}
              {incident.status === 'acknowledged' && (
                <span className="wt-chip wt-chip-ack">
                  ack {incident.acknowledgedBy}
                  {incident.acknowledgedAt ? ` · ${ago(incident.acknowledgedAt)}` : ''}
                </span>
              )}
            </div>
            <div className="wt-inc-summary">{incident.summary}</div>
            {incident.remedy && <div className="wt-remedy">→ {incident.remedy}</div>}
            <div className="wt-facts">
              {Object.entries(incident.facts || {})
                .filter(([key]) => !key.startsWith('_'))
                .slice(0, 6)
                .map(([key, value]) => (
                  <span key={key}>
                    <b>{key}</b> {String(value)}
                  </span>
                ))}
            </div>
            {problems[incident.id] && (
              <div className="wt-inc-problem" role="alert">
                {problems[incident.id]}
              </div>
            )}
          </div>
          <div className="wt-inc-side">
            <span className="wt-when">{ago(incident.lastSeenAt)}</span>
            <span className="wt-when">
              since {ago(incident.firstSeenAt)} · ×{incident.occurrences}
            </span>
            {canRespond && incident.status === 'open' && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy === incident.id}
                aria-label={`Acknowledge: ${incident.title}`}
                onClick={() => void acknowledge(incident)}
              >
                Acknowledge
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              aria-label={`Details: ${incident.title}`}
              onClick={() => setOpenId(incident.id)}
            >
              {canRespond ? 'Silence, resolve' : 'Details'} <ChevronRight size={13} />
            </button>
          </div>
        </article>
      ))}
      {drawer}
    </>
  );
}
